export const LINE_TYPE = {
  LOW: 'low_voltage',
  MEDIUM: 'medium_voltage',
  HIGH: 'high_voltage',
  TRANSMISSION: 'transmission_line',
  TRANSFORMER: 'transformer_pole',
} as const;

export const PLOT_TYPE = {
  AGRICULTURAL: 'agricultural',
  RESIDENTIAL_BUILDING: 'residential_building',
  SERVICE_BUILDING: 'service_building',
  INDUSTRIAL: 'industrial',
  FOREST: 'forest',
  NO_MPZP: 'no_mpzp',
  UNKNOWN: 'unknown',
} as const;

export const DOCUMENT_TYPE = {
  TEMPLATE: 'template',
  ONLINE: 'online',
} as const;

export const ADDON_TYPE = {
  PDF_INSTRUCTION: 'pdf-instruction',
  CONSULTATION: 'consultation',
} as const;

export const ADDON_LABELS: Record<
  (typeof ADDON_TYPE)[keyof typeof ADDON_TYPE],
  string
> = {
  [ADDON_TYPE.PDF_INSTRUCTION]: 'Instrukcja PDF',
  [ADDON_TYPE.CONSULTATION]: 'Konsultacja z prawnikiem',
};

export type LineType = (typeof LINE_TYPE)[keyof typeof LINE_TYPE];
export type PlotType = (typeof PLOT_TYPE)[keyof typeof PLOT_TYPE];
export type DocumentType = (typeof DOCUMENT_TYPE)[keyof typeof DOCUMENT_TYPE];
export type AddonType = (typeof ADDON_TYPE)[keyof typeof ADDON_TYPE];

export interface EcommerceFormValues {
  // Screen 1 — plot info
  line_types: LineType[];
  plot_type: PlotType | '';
  co_owners_count: string;
  // Screen 2 — contact details
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  privacy_policy: boolean;
  // Screen 3 — document selection
  document_type: DocumentType | '';
  addons: AddonType[];
  // Screen 4 — billing data
  billing_first_name: string;
  billing_last_name: string;
  billing_phone: string;
  billing_email: string;
  street: string;
  building_number: string;
  apartment_number: string;
  postal_code: string;
  city: string;
  is_company: boolean | null;
  company_name: string;
  tax_id: string;
  invoice_street: string;
  invoice_building_number: string;
  invoice_apartment_number: string;
  invoice_postal_code: string;
  invoice_city: string;
  payment_method: string;
}

export const ECOMMERCE_DEFAULT_VALUES: EcommerceFormValues = {
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  privacy_policy: false,
  line_types: [],
  plot_type: '',
  co_owners_count: '',
  document_type: '',
  addons: [],
  billing_first_name: '',
  billing_last_name: '',
  billing_phone: '',
  billing_email: '',
  street: '',
  building_number: '',
  apartment_number: '',
  postal_code: '',
  city: '',
  is_company: null,
  company_name: '',
  tax_id: '',
  invoice_street: '',
  invoice_building_number: '',
  invoice_apartment_number: '',
  invoice_postal_code: '',
  invoice_city: '',
  payment_method: '',
};

export const DOCUMENT_PRICES: Record<DocumentType, number> = {
  [DOCUMENT_TYPE.TEMPLATE]: 399,
  [DOCUMENT_TYPE.ONLINE]: 400,
};

export const ADDON_PRICES: Record<AddonType, number> = {
  [ADDON_TYPE.PDF_INSTRUCTION]: 49,
  [ADDON_TYPE.CONSULTATION]: 249,
};

// Fields to validate before advancing from each screen. The screens of the
// second funnel (document, billing) declare theirs through `EcommerceSchema`
// — see stepsSchema.tsx — so keep these lists named and reuse them there.
export const PLOT_FIELDS: (keyof EcommerceFormValues)[] = [
  'line_types',
  'plot_type',
  'co_owners_count',
];

export const CONTACT_FIELDS: (keyof EcommerceFormValues)[] = [
  'first_name',
  'last_name',
  'phone',
  'email',
  'privacy_policy',
];

export const DOCUMENT_FIELDS: (keyof EcommerceFormValues)[] = ['document_type'];

export const BILLING_FIELDS: (keyof EcommerceFormValues)[] = [
  'billing_first_name',
  'billing_last_name',
  'billing_phone',
  'billing_email',
  'street',
  'building_number',
  'postal_code',
  'city',
  'is_company',
  'company_name',
  'tax_id',
  'invoice_street',
  'invoice_building_number',
  'invoice_postal_code',
  'invoice_city',
];

// Keyed by the pre-sale funnel's own screen counter (TransmissionCreateLeadForm).
export const SCREEN_FIELDS: Partial<
  Record<number, (keyof EcommerceFormValues)[]>
> = {
  1: PLOT_FIELDS,
  2: CONTACT_FIELDS,
  3: DOCUMENT_FIELDS,
  4: BILLING_FIELDS,
};
